import * as React from "react";
import {
  Accessibility,
  CalendarSync,
  FileText,
  Gift,
  GlobeLock,
  Headset,
  Home,
  Ticket,
  UserX,
  Wallet,
} from "lucide-react";

import NavUser from "@/components/NavUser";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router";
import { useClient } from "@/hooks/useClient.ts";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { usePrizeCompetition } from "@/hooks/usePrizeCompetition.ts";
import { useTranslation } from "@/i18n";

const data = {
  navMain: [
    {
      titleKey: "nav.raffles",
      url: "/",
      icon: Home,
    },
    {
      titleKey: "nav.freeTicket",
      url: "/free-ticket",
      icon: Gift,
    },
    {
      titleKey: "nav.wallet",
      url: "/wallet",
      icon: Wallet,
    },
    {
      titleKey: "nav.tickets",
      url: "/tickets",
      icon: Ticket,
    },
    {
      titleKey: "nav.subscriptions",
      url: "/subscriptions",
      icon: CalendarSync,
    },
    {
      titleKey: "nav.support",
      url: "/support",
      icon: Headset,
    },
  ],

  compliance: [
    {
      titleKey: "nav.termsOfService",
      url: "/terms-of-service",
      icon: FileText,
    },
    {
      titleKey: "nav.gdpr",
      url: "/gdpr",
      icon: FileText,
    },
    {
      titleKey: "nav.privacyPolicy",
      url: "/privacy-policy",
      icon: GlobeLock,
    },
    {
      titleKey: "nav.accessibility",
      url: "/accessibility",
      icon: Accessibility,
    },
    {
      titleKey: "nav.complaints",
      url: "/complaints",
      icon: UserX,
    },
  ],
} as const;

const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const location = useLocation();
  const isActive = (path: string) =>
    location.pathname === path ||
    (path.length > 1 && location.pathname.startsWith(path));
  const client = useClient();
  const { t } = useTranslation();
  const { claimInstruction } = usePrizeCompetition();
  const navMain = data.navMain.filter(
    (item) => item.url !== "/free-ticket" || !!claimInstruction,
  );

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <img
                  className="mx-auto max-h-12 rounded object-contain"
                  src={client!.logo_url}
                  alt={client!.name}
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <ScrollArea className="h-full flex-grow">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navMain.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                      <Link to={item.url}>
                        <item.icon />
                        {t(item.titleKey)}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.compliance")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {data.compliance.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                      <Link to={item.url}>
                        <item.icon />
                        {t(item.titleKey)}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
